import apiClient from '../utils/api';
import { CategoryData } from '../types/category';

export interface CategoryDto {
  categoryId: string;
  name: string;
  iconURL: string;
  displayOrder: number;
  isActive: boolean;
  subCategories: SubcategoryDto[];
  createdAt: string;
  updatedAt: string;
}

export interface SubcategoryDto {
  /** Stable catalog map key — use as selector value when present */
  key?: string;
  name: string;
  displayOrder: number;
  isActive: boolean;
  iconURL?: string;
  types: TypeDto[];
}

export interface TypeDto {
  /** Stable catalog map key — use as selector value when present */
  key?: string;
  name: string;
  displayOrder: number;
  isActive: boolean;
  items: string[];
  description: string;
}

export interface CategoriesResponse {
  message: string;
  data: {
    categories: CategoryDto[];
    count: number;
    pagination: {
      hasMore: boolean;
    };
  };
  timestamp: string;
}

export interface CategoryResponse {
  message: string;
  data: {
    category: CategoryDto;
  };
  timestamp: string;
}

/** Maps catalog IDs/keys → display names. Request payloads still use keys/IDs. */
export interface CategoryLabelMaps {
  mainById: Record<string, string>;
  subByKey: Record<string, string>;
  typeByKey: Record<string, string>;
}

/** Match backend generateKeyFromName (fallback when API omits `key`). */
export const generateKeyFromName = (name: string): string =>
  name
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

export function buildCategoryLabelMaps(categories: CategoryDto[]): CategoryLabelMaps {
  const mainById: Record<string, string> = {};
  const subByKey: Record<string, string> = {};
  const typeByKey: Record<string, string> = {};

  for (const category of categories) {
    mainById[category.categoryId] = category.name;
    // Legacy local state sometimes stored the display name
    mainById[category.name] = category.name;

    for (const sub of category.subCategories || []) {
      const subKey = sub.key || generateKeyFromName(sub.name);
      subByKey[subKey] = sub.name;
      subByKey[sub.name] = sub.name;

      for (const type of sub.types || []) {
        const typeKey = type.key || generateKeyFromName(type.name);
        typeByKey[typeKey] = type.name;
        typeByKey[type.name] = type.name;
      }
    }
  }

  return { mainById, subByKey, typeByKey };
}

/** Resolve stored keys/IDs to readable labels; unknown values pass through. */
export function formatCategoryLabels(
  values: string[] | undefined | null,
  map: Record<string, string>,
): string {
  if (!values || values.length === 0) return '';
  return values.map((v) => map[v] || v).join(', ');
}

export const categoryService = {
  /**
   * Get all categories from the API (vendor authenticated)
   */
  getAllCategories: async (): Promise<CategoriesResponse> => {
    const response = await apiClient.get<CategoriesResponse>('/vendors/categories-catalogue');
    return response.data;
  },

  /**
   * Get a specific category by ID (vendor authenticated)
   */
  getCategory: async (categoryId: string): Promise<CategoryResponse> => {
    const response = await apiClient.get<CategoryResponse>(`/vendors/categories-catalogue/${categoryId}`);
    return response.data;
  },

  /**
   * Load catalog once and build ID/key → name maps for display-only use.
   */
  getCategoryLabelMaps: async (): Promise<CategoryLabelMaps> => {
    const response = await categoryService.getAllCategories();
    return buildCategoryLabelMaps(response.data?.categories || []);
  },

  /**
   * Transform categories data to the format expected by CategoryTreeSelector
   */
  transformCategoriesForSelector: (response: CategoriesResponse): CategoryData => {
    if (!response.data.categories || response.data.categories.length === 0) {
      return {
        mainCategories: [],
        subCategoryOptions: [],
        allTypeOptions: [],
        itemOptions: {}
      };
    }

    const categories = response.data.categories;
    
    // Transform all active categories into main category options
    const mainCategories = categories
      .filter(cat => cat.isActive)
      .map(cat => ({
        value: cat.categoryId,
        label: cat.name
      }));

    // Transform subcategories with category information
    const subCategoryOptions: Array<{ value: string; label: string; categoryId: string }> = [];
    const allTypeOptions: Array<{ value: string; label: string; subCategory: string; categoryId: string }> = [];
    const itemOptions: Record<string, string[]> = {};

    // Track seen keys to ensure options are unique
    const seenSubCategories = new Set<string>();
    const seenTypes = new Set<string>();
    const itemSets: Record<string, Set<string>> = {};

    categories.forEach(category => {
      if (!category.isActive) return;

      category.subCategories.forEach((subCategory) => {
        if (!subCategory.isActive) return;

        const subKey = subCategory.key || generateKeyFromName(subCategory.name);

        // Only add each subcategory option once
        if (!seenSubCategories.has(subKey)) {
          seenSubCategories.add(subKey);
          subCategoryOptions.push({
            value: subKey,
            label: subCategory.name,
            categoryId: category.categoryId
          });
        }

        subCategory.types.forEach((type) => {
          if (!type.isActive) return;

          const typeKey = type.key || generateKeyFromName(type.name);

          // Only add each type option once
          if (!seenTypes.has(typeKey)) {
            seenTypes.add(typeKey);
            allTypeOptions.push({
              value: typeKey,
              label: type.name,
              subCategory: subKey,
              categoryId: category.categoryId
            });
          }

          if (type.items && type.items.length > 0) {
            if (!itemSets[typeKey]) {
              itemSets[typeKey] = new Set<string>();
            }
            type.items.forEach(item => itemSets[typeKey].add(item));
          }
        });
      });
    });

    // Convert itemSets back to plain arrays, ensuring uniqueness
    Object.entries(itemSets).forEach(([typeKey, itemsSet]) => {
      itemOptions[typeKey] = Array.from(itemsSet);
    });

    return {
      mainCategories,
      subCategoryOptions,
      allTypeOptions,
      itemOptions
    };
  }
};
